<template>
    <div class="announcement-manager">
        <el-card shadow="never" class="content-card">
            <template #header>
                <div class="card-header">
                    <div class="header-title">
                        <el-icon>
                            <Bell />
                        </el-icon>
                        <span>系统公告管理</span>
                    </div>
                    <el-button type="primary" @click="openCreateDialog">
                        <el-icon>
                            <Plus />
                        </el-icon> 新建公告
                    </el-button>
                </div>
            </template>

            <el-table :data="announcements" v-loading="loading" style="width: 100%" class="custom-table">
                <el-table-column prop="id" label="ID" width="70" align="center" />
                <el-table-column prop="title" label="标题" min-width="180" show-overflow-tooltip />
                <el-table-column label="标签" width="120" align="center">
                    <template #default="{ row }">
                        <el-tag v-if="row.tag" :type="row.tagType || 'info'" size="small" effect="dark" round>
                            {{ row.tag }}
                        </el-tag>
                        <span v-else>-</span>
                    </template>
                </el-table-column>
                <el-table-column prop="sortOrder" label="排序" width="80" align="center" />
                <el-table-column label="状态" width="120" align="center">
                    <template #default="{ row }">
                        <el-switch v-model="row.isPublished" @change="handleTogglePublish(row)" active-text="发布"
                            inactive-text="草稿" inline-prompt />
                    </template>
                </el-table-column>
                <el-table-column label="创建时间" width="170" align="center">
                    <template #default="{ row }">
                        {{ formatDate(row.createdAt) }}
                    </template>
                </el-table-column>
                <el-table-column label="操作" width="140" fixed="right" align="center">
                    <template #default="{ row }">
                        <el-button link type="primary" @click="openEditDialog(row)">编辑</el-button>
                        <el-button link type="danger" @click="handleDelete(row)">删除</el-button>
                    </template>
                </el-table-column>
            </el-table>
        </el-card>

        <!-- Create / Edit Dialog -->
        <el-dialog v-model="dialogVisible" :title="isEdit ? '编辑公告' : '新建公告'" width="750px" class="custom-dialog"
            destroy-on-close top="5vh">
            <el-form :model="form" label-position="top">
                <el-form-item label="公告标题" required>
                    <el-input v-model="form.title" placeholder="例如：系统将于周六进行维护" />
                </el-form-item>
                <el-row :gutter="16">
                    <el-col :span="8">
                        <el-form-item label="标签文字">
                            <el-input v-model="form.tag" placeholder="如：通知、新功能、活动" />
                        </el-form-item>
                    </el-col>
                    <el-col :span="8">
                        <el-form-item label="标签颜色">
                            <el-select v-model="form.tagType" placeholder="选择颜色" style="width: 100%;">
                                <el-option label="蓝色 (primary)" value="primary" />
                                <el-option label="绿色 (success)" value="success" />
                                <el-option label="橙色 (warning)" value="warning" />
                                <el-option label="红色 (danger)" value="danger" />
                                <el-option label="灰色 (info)" value="info" />
                            </el-select>
                        </el-form-item>
                    </el-col>
                    <el-col :span="8">
                        <el-form-item label="排序 (越大越靠前)">
                            <el-input-number v-model="form.sortOrder" :min="0" :max="9999" style="width: 100%;" />
                        </el-form-item>
                    </el-col>
                </el-row>
                <el-form-item label="公告内容 (支持 Markdown)">
                    <MdEditor v-model="form.content" :style="{ height: '300px' }" />
                </el-form-item>
                <el-form-item label="发布状态">
                    <el-switch v-model="form.isPublished" active-text="发布" inactive-text="草稿" />
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="dialogVisible = false">取消</el-button>
                <el-button type="primary" :loading="saving" @click="handleSave">
                    {{ isEdit ? '保存修改' : '立即创建' }}
                </el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus, Bell } from '@element-plus/icons-vue'
import MdEditor from 'md-editor-v3'
import 'md-editor-v3/lib/style.css'
import api from '@/utils/api'

const loading = ref(false)
const saving = ref(false)
const announcements = ref([])
const dialogVisible = ref(false)
const isEdit = ref(false)
const editId = ref(null)

const form = reactive({
    title: '',
    content: '',
    tag: '',
    tagType: 'primary',
    sortOrder: 0,
    isPublished: false
})

const resetForm = () => {
    form.title = ''
    form.content = ''
    form.tag = ''
    form.tagType = 'primary'
    form.sortOrder = 0
    form.isPublished = false
}

const fetchAnnouncements = async () => {
    loading.value = true
    try {
        const res = await api.get('/api/admin/announcements')
        announcements.value = res.data.announcements || []
    } catch (err) {
        ElMessage.error('获取公告列表失败')
    } finally {
        loading.value = false
    }
}

const openCreateDialog = () => {
    isEdit.value = false
    editId.value = null
    resetForm()
    dialogVisible.value = true
}

const openEditDialog = (row) => {
    isEdit.value = true
    editId.value = row.id
    form.title = row.title
    form.content = row.content
    form.tag = row.tag
    form.tagType = row.tagType
    form.sortOrder = row.sortOrder
    form.isPublished = row.isPublished
    dialogVisible.value = true
}

const handleSave = async () => {
    if (!form.title.trim()) {
        return ElMessage.warning('请输入公告标题')
    }
    saving.value = true
    try {
        if (isEdit.value) {
            await api.put(`/api/admin/announcements/${editId.value}`, { ...form })
            ElMessage.success('更新成功')
        } else {
            await api.post('/api/admin/announcements', { ...form })
            ElMessage.success('创建成功')
        }
        dialogVisible.value = false
        fetchAnnouncements()
    } catch (err) {
        ElMessage.error(isEdit.value ? '更新失败' : '创建失败')
    } finally {
        saving.value = false
    }
}

const handleTogglePublish = async (row) => {
    try {
        await api.put(`/api/admin/announcements/${row.id}`, { ...row })
        ElMessage.success(row.isPublished ? '已发布' : '已转为草稿')
    } catch (err) {
        row.isPublished = !row.isPublished
        ElMessage.error('操作失败')
    }
}

const handleDelete = (row) => {
    ElMessageBox.confirm('确定要删除该公告吗？此操作不可撤销。', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
    }).then(async () => {
        await api.delete(`/api/admin/announcements/${row.id}`)
        ElMessage.success('删除成功')
        fetchAnnouncements()
    }).catch(() => { })
}

const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    const d = new Date(dateStr)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

onMounted(() => {
    fetchAnnouncements()
})
</script>

<style scoped>
.announcement-manager {
    padding: 0;
}

.content-card {
    border-radius: 12px;
    border: 1px solid #e6e6e6;
}

.card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.header-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 16px;
    font-weight: 700;
    color: #303133;
}

.custom-table :deep(.el-table__header th) {
    background-color: #fafafa;
    color: #606266;
    font-weight: 600;
}
</style>
